import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import api from "../../api.js";
import "../../CSS/Branch.css";
import { useRights } from "../../Components/RightsContext.jsx"; // adjust path to wherever RightsContext.jsx actually lives
 
// Must match this screen's Submenus.pathname exactly (check the DB value —
// this is the same kind of string that caused the Party Master bug earlier).
const BRANCH_PATH = "Master/Branch";

// ── INITIAL DATA (Now empty, will be fetched from API) ───────────────────────────────
const initialBranches = [];


// ── UI COMPONENTS ───────────────────────────────
function Toast({ toast }) {
  if (!toast.show) return null;
  return createPortal(
    <div className={`toast show ${toast.type || ""}`} style={{ zIndex: 99999 }}>
      <span>{toast.msg}</span>
    </div>,
    document.body
  );
}

function DeleteOverlay({ pending, onConfirm, onClose }) {
  if (!pending) return null;
  return createPortal(
    <div className="del-overlay show" style={{ zIndex: 10000 }}>
      <div className="del-modal">
        <div className="d-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="26" height="26">
            <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
            <path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
          </svg>
        </div>
        <h3>Delete Record?</h3>
        <p>{pending.msg}</p>
        <div className="del-actions">
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" style={{ flex: 1 }} onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Modal({ modal, onClose, onSave, showToast, branches, states }) {
  const { id } = modal;
  const rec = id ? branches.find((r) => r.branch_Prefix === id) || {} : {};

  const [form, setForm] = useState(() => buildBranchInitialForm(rec));
  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  function handleSave() {
    const built = validateAndBuildBranchRecord(form, rec, showToast);
    if (built === null) return;
    onSave(id, built);
  }

  return createPortal(
    <div className="branch-master-overlay show">
      <div className="modal">
        <div className="modal-head">
          <h3>{id ? "Edit Branch" : "Add Branch"}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <BranchForm form={form} set={set} states={states} />
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15">
              {id ? (
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
              ) : (
                <>
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </>
              )}
            </svg>
            {id ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── HELPERS ────────────────────────────────────
function statusBadge(s) {
  return (s === "active" || s === "A") ? (
    <span className="td-badge badge-active">● Active</span>
  ) : (
    <span className="td-badge badge-inactive">● Inactive</span>
  );
}

function ActionButtons({ onEdit, onToggle, onDelete, status,rights }) {
  return (
    <div className="row-actions">
      {rights.edit && (
      <button className="icon-btn edit" onClick={onEdit} title="Edit">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>
      )}
      <button className="icon-btn toggle" onClick={onToggle} title={(status === "active" || status === "A") ? "Deactivate" : "Activate"}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18.36 6.64a9 9 0 11-12.73 0" />
          <line x1="12" y1="2" x2="12" y2="12" />
        </svg>
      </button>
       {rights.delete && (
      <button className="icon-btn del" onClick={onDelete} title="Delete">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
        </svg>
      </button>
    )} 
    </div>
  );
}

function F({ label, req, hint, children, cls }) {
  return (
    <div className={`field ${cls || ""}`}>
      <label className={req ? "field-req" : ""}>{label}</label>
      {children}
      {hint && <div className="field-hint">{hint}</div>}
    </div>
  );
}

const onlyText = (value) => {
  return value.replace(/[^a-zA-Z\s]/g, "");
};

const onlyCode = (value) => {
  return value.replace(/[^a-zA-Z]/g, "").slice(0, 3).toUpperCase();
};

const onlyNumber = (value) => {
  return value.replace(/\D/g, "").slice(0, 10);
};

function BranchForm({ form, set, states }) {
  return (
    <>
      <div className="grid-2">
        <F label="BRANCH CODE" req>
          <input
            value={form.Branch_Prefix}
            // onChange={(e) => set("Branch_Prefix", e.target.value.toUpperCase())}
            onChange={(e) => set("Branch_Prefix", onlyCode(e.target.value))}
            maxLength={3}
            placeholder="E.G. MUM"
            // maxLength={6}
            style={{ textTransform: "uppercase" }}
          />
        </F>
        <F label="BRANCH NAME" req>
          <input
            value={form.Place_name}
            // onChange={(e) => set("Place_name", e.target.value)}
            onChange={(e) => set("Place_name", onlyText(e.target.value))}
            placeholder="Full branch name"
            autoComplete="off"
          />
        </F>
        <F label="CITY" req>
          <input
            value={form.City}
            // onChange={(e) => set("City", e.target.value)}
            onChange={(e) => set("City", onlyText(e.target.value))}
            placeholder="City"
            autoComplete="off"
          />
        </F>
        <F label="STATE" req>
          <select
            value={form.State_Code}
            onChange={(e) => set("State_Code", e.target.value)}
          >
            <option value="">-- SELECT STATE --</option>
            {states && states.map(s => (
              <option key={s.State_id} value={s.State_id}>{s.State_Name}</option>
            ))}
          </select>
        </F>
        <F label="BRANCH ADDRESS" cls="col-2">
          <textarea
            value={form.Place_off_address}
            onChange={(e) => set("Place_off_address", e.target.value)}
            rows={2}
            style={{ resize: "vertical" }}
            autoComplete="off"
          />
          <div className="field-hint">Full address for this location</div>
        </F>
        <F label="PINCODE">
          <input
            value={form.Pincode}
            onChange={(e) => set("Pincode", e.target.value.replace(/\D/g, ""))}
            placeholder="6-digit PIN"
            maxLength={6}
            autoComplete="off"
          />
        </F>
        <F label="BRANCH EMAIL">
          <input
            value={form.Email}
            onChange={(e) => set("Email", e.target.value)}
            placeholder="branch@saamitrade.in"
          />
        </F>
        <F label="PRIMARY CONTACT">
          <input
            value={form.Place_Contact}
            onChange={(e) => set("Place_Contact", onlyNumber(e.target.value))}
            placeholder="Primary phone number"
          />
        </F>
        <F label="SECONDARY PHONE">
          <input
            value={form.Place_Phone}
            // onChange={(e) => set("Place_Phone", e.target.value)}
            onChange={(e) => set("Place_Phone", onlyNumber(e.target.value))}
            placeholder="Optional secondary number"
          />
        </F>
        <F label="MANAGER NAME" cls="col-2">
          <input
            value={form.Manager_Name}
            onChange={(e) => set("Manager_Name", onlyText(e.target.value))}
            placeholder="Branch manager"
          />
        </F>
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <label className="field-req">LOCATION TYPE</label>
        <div className="branch-type-row">
          <div
            className={`btype-card ${form.Place_Type === "Branch" || form.Place_Type === "B" ? "active" : ""}`}
            onClick={() => set("Place_Type", "Branch")}
          >
            <div className="btype-icon">🏢</div>
            <div className="btype-label">Branch</div>
            <div className="btype-sub">Can book & dispatch consignments</div>
          </div>
          <div
            className={`btype-card ${form.Place_Type === "Delivery" || form.Place_Type === "D" ? "active" : ""}`}
            onClick={() => { set("Place_Type", "Delivery"); set("Billing_Configuration_Toggle", "F"); }}
          >
            <div className="btype-icon">📬</div>
            <div className="btype-label">Delivery Place Only</div>
            <div className="btype-sub">Destination only — no booking</div>
          </div>
        </div>
      </div>

      {form.Place_Type !== "Delivery" && form.Place_Type !== "D" && (
        <>
          <div className="cond-divider"><span>BILLING CONFIGURATION</span></div>
          <div className="billing-toggle-row">
            <div className="btog-left">
              <div className="btog-icon">💳</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>Billing Allowed from this Branch</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Enable to allow invoicing & billing operations</div>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={form.Billing_Configuration_Toggle === "O" || form.Billing_Configuration_Toggle === true || form.Billing_Configuration_Toggle === "true"}
                onChange={(e) => set("Billing_Configuration_Toggle", e.target.checked ? "O" : "F")}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {form.Billing_Configuration_Toggle === "O" && (
            <>
              <div className="cond-sub-block" style={{ marginTop: 12, overflow: "visible" }}>
                <div className="csb-header"><span className="csb-icon">📜</span><span>GST Registration</span></div>
                <div className="grid-1" style={{ padding: 14 }}>
                  <F label="GSTIN OF THIS BRANCH" req>
                    <input
                      value={form.GST_Number}
                      onChange={(e) => set("GST_Number", e.target.value.toUpperCase())}
                      placeholder="E.G. 27AABCS1234A1Z5"
                      maxLength={15}
                      style={{ textTransform: "uppercase" }}
                    />
                    <div className="field-hint">15-character GST Identification Number for this location</div>
                  </F>
                </div>
              </div>

              <div className="cond-sub-block" style={{ marginTop: 14, overflow: "visible" }}>
                <div className="csb-header" style={{ justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="csb-icon" style={{ color: "#f9a825" }}>⚡</span>
                    <span>e-Invoice API Configuration</span>
                  </div>
                  <label className="toggle-switch" style={{ transform: "scale(0.85)" }}>
                    <input
                      type="checkbox"
                      checked={form.API_Configuration_Toggle === "O" || form.API_Configuration_Toggle === true || form.API_Configuration_Toggle === "true"}
                      onChange={(e) => set("API_Configuration_Toggle", e.target.checked ? "O" : "F")}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                {form.API_Configuration_Toggle === "O" && (
                  <div style={{ padding: 14 }}>
                    <div className="grid-2">
                      <F label="API ENDPOINT URL" req cls="col-2">
                        <input value={form.API_URL_Link} onChange={(e) => set("API_URL_Link", e.target.value)} />
                      </F>
                      <F label="API USERNAME" req>
                        <input
                          autoComplete="new-password"
                          value={form.API_Username}
                          onChange={(e) => set("API_Username", e.target.value)}
                        />
                      </F>
                      <F label="API PASSWORD" req>
                        <input
                          type="password"
                          autoComplete="new-password"
                          value={form.API_password}
                          onChange={(e) => set("API_password", e.target.value)}
                        />
                      </F>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      <div className="cond-divider"><span>RECORD STATUS</span></div>
      <F label="STATUS">
        <select value={form.Branch_Status} onChange={(e) => set("Branch_Status", e.target.value)}>
          <option value="A">Active</option>
          <option value="I">Inactive</option>
        </select>
      </F>
    </>
  );
}


// ── MAIN COMPONENT ─────────────────────────────
export default function Branch() {
  const rights = useRights(BRANCH_PATH);
  const [branches, setBranches] = useState(initialBranches);
  const [modal, setModal] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [toast, setToast] = useState({ show: false, msg: "", type: "" });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const toastTimer = useRef(null);

  function showToast(msg, type = "success") {
    setToast({ show: true, msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(t => ({ ...t, show: false })), 2800);
  }

  const [states, setStates] = useState([]);
  useEffect(() => {
    fetchBranches();
    fetchStates();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/get-branches');
      setBranches(response.data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await api.get('/dropdown?mode=STE');
      setStates(response.data);
    } catch (error) {
      console.error("Failed to fetch states:", error);
    }
  };

  const handleSave = async (id, rec) => {
    try {
      const payload = [rec]; // Backend expects a list
      await api.post('/insert-branches', payload);
      showToast(id ? "Branch updated successfully" : "Branch added successfully");
      alert(id? "Branch updated successfully" : "Branch added successfully");
      fetchBranches();
      setModal(null);
    } catch (error) {
      showToast("Error saving branch", "error");
      alert("Error saving branch: " + (error.response?.data?.message || error.message));
    }
  };

  const handleToggle = async (id) => {
    const rec = branches.find(r => r.branch_Prefix === id);
    if (!rec) return;

    const currentStatus = rec.branch_Status || rec.Branch_Status;
    const newStatus = currentStatus === "A" ? "I" : "A";

    // Send the full existing record with just the status flipped,
    // same shape handleSave already uses for /insert-branches
    const payload = [{ ...rec, Branch_Status: newStatus, branch_Status: newStatus }];

    try {
      await api.post('/insert-branches', payload);
      setBranches(prev =>
        prev.map(r =>
          r.branch_Prefix === id
            ? { ...r, branch_Status: newStatus, Branch_Status: newStatus }
            : r
        )
      );
      showToast(newStatus === "A" ? "Branch activated" : "Branch deactivated");
    } catch (error) {
      showToast("Error updating status", "error");
      alert("Error updating status: " + (error.response?.data?.message || error.message));
    }
  };

  function handleDelete(id) {
    const r = branches.find(b => b.branch_Prefix === id);
    setPendingDelete({ id, msg: `Delete branch "${r?.place_name}"?` });
  }

  function confirmDelete() {
    setBranches(prev => prev.filter(r => r.branch_Prefix !== pendingDelete.id));
    setPendingDelete(null);
    showToast("Branch deleted", "error");
    alert("Branch deleted");
  }

  const handleStatusFilter = (val) => {
    setStatusFilter(val);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const filtered = branches.filter(r => {
    const text = Object.values(r).join(" ").toLowerCase();
    const matchQ = !search || text.includes(search.toLowerCase());
    const matchS = statusFilter === "all" || (statusFilter === "active" ? (r.branch_Status || r.Branch_Status) === "A" : (r.branch_Status || r.Branch_Status) === "I");
    return matchQ && matchS;
  // });
  // }).sort((a, b) =>
  //     (a.place_name || a.Place_name || "").localeCompare(b.place_name || b.Place_name || "", undefined, { sensitivity: "base" })
  //   );
  }).sort((a, b) => {
  const nameA = (a.place_name || a.Place_name || "").trim();
  const nameB = (b.place_name || b.Place_name || "").trim();
  if (!nameA && !nameB) return 0;
  if (!nameA) return 1;   // blanks go to the end
  if (!nameB) return -1;
  return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const pageItems = getPaginationRange(currentPage, totalPages);

  return (
    <>
      <div className="master-panel active">
        <div className="panel-header">
          <div className="panel-title">
            <h2>Branches / Booking Places</h2>
            <p>Manage origin and destination branch locations</p>
          </div>
          <div className="panel-actions">
            {rights.add && (
            <button
              className="btn btn-primary"
              onClick={() => setModal({ id: null })}
              style={{ cursor: "pointer", position: "relative", zIndex: 10 }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                <path d="M12 5v14M5 12h14" />
              </svg> Add Branch
            </button>
            )}
          </div>
        </div>

        <div className="search-row">
          <div className="search-box">
            <span className="s-icon">🔍</span>
            <input type="text" placeholder="Search branches…" value={search} onChange={handleSearchChange} />
          </div>
          {["all", "active", "inactive"].map(f => (
            <span key={f} className={`filter-chip ${statusFilter === f ? "active" : ""}`} onClick={() => handleStatusFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </span>
          ))}
        </div>

        <div className="table-card">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>SR.NO</th><th>Code</th><th>Branch Name</th><th>City</th><th>State</th><th>Manager</th><th>Type</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr><td colSpan={9}><div className="empty-state">No records found</div></td></tr>
                ) : (
                  paginatedData.map((r, i) => (
                    <tr key={r.branch_Prefix}>
                      <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                      <td style={{ fontWeight: 700, color: "var(--branch-blue)", letterSpacing: "0.5px" }}>{r.branch_Prefix}</td>
                      <td><strong>{r.place_name}</strong></td>
                      <td>{r.city || r.City}</td>
                      <td className="td-muted">
                        {states.find(s => String(s.State_id) === String(r.state_Code))?.State_Name || r.state_Code || "N/A"}
                      </td>
                      <td>{r.manager_Name || r.Manager_Name || "N/A"}</td>
                      <td>
                        <span className={`td-badge ${r.place_Type === "Branch" || r.place_Type === "B" || r.place_Type === "branch" ? "badge-blue" : "badge-amber"}`}>
                          {r.place_Type === "Branch" || r.place_Type === "B" || r.place_Type === "branch" ? "Branch" : "Delivery"}
                        </span>
                      </td>
                      <td>{statusBadge(r.branch_Status || r.Branch_Status)}</td>
                      <td>
                        <ActionButtons
                          onEdit={() => setModal({ id: r.branch_Prefix })}
                          onToggle={() => handleToggle(r.branch_Prefix)}
                          onDelete={() => handleDelete(r.branch_Prefix)}
                          status={r.branch_Status || r.Branch_Status}
                          rights={rights}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <div className="pg-info">
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length} entries
              </div>
              <div className="pg-btns">
                <button
                  className="pg-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {pageItems.map((item, idx) =>
                  item === "..." ? (
                    <span key={`dots-${idx}`} className="pg-ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`pg-btn ${currentPage === item ? "active" : ""}`}
                      onClick={() => setCurrentPage(item)}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  className="pg-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <Modal
          modal={modal}
          branches={branches}
          onClose={() => setModal(null)}
          onSave={handleSave}
          showToast={showToast}
          states={states}
        />
      )}
      {pendingDelete && (
        <DeleteOverlay
          pending={pendingDelete}
          onConfirm={confirmDelete}
          onClose={() => setPendingDelete(null)}
        />
      )}
      <Toast toast={toast} />
    </>
  );
}

// ── LOGIC HELPERS ────────────────────────────────
// Builds a windowed page-number list so the pagination bar stays a fixed,
// reasonable width no matter how many total pages exist (e.g. 2,600+ pages
// for 15,000+ records). Always shows first page, last page, current page
// and a couple of neighbors on each side, collapsing the rest into "...".
// Example output for currentPage=150, totalPages=2600:
//   [1, "...", 148, 149, 150, 151, 152, "...", 2600]
function getPaginationRange(currentPage, totalPages, siblingCount = 1) {
  const totalNumbersToShow = siblingCount * 2 + 5; // first, last, current, 2*siblings, +2 for ellipses slots

  if (totalPages <= totalNumbersToShow) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

  const pages = [1];

  if (shouldShowLeftEllipsis) {
    pages.push("...");
  } else {
    for (let i = 2; i < leftSiblingIndex; i++) pages.push(i);
  }

  for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
    if (i !== 1 && i !== totalPages) pages.push(i);
  }

  if (shouldShowRightEllipsis) {
    pages.push("...");
  } else {
    for (let i = rightSiblingIndex + 1; i < totalPages; i++) pages.push(i);
  }

  pages.push(totalPages);

  return pages;
}

function buildBranchInitialForm(rec) {
  return {
    Place_Id: rec.place_Id || rec.Place_Id || 0,
    Branch_Prefix: rec.branch_Prefix || rec.Branch_Prefix || "",
    Place_name: rec.place_name || rec.Place_name || "",
    City: rec.city || rec.City || "",
    State_Code: rec.state_Code || rec.State_Code || "",
    Place_off_address: rec.place_off_address || rec.Place_off_address || "",
    Pincode: rec.pincode || rec.Pincode || "",
    Email: rec.email || rec.Email || "",
    Place_Phone: rec.place_Phone || rec.Place_Phone || "",
    Place_Contact: rec.place_Contact || rec.Place_Contact || "",
    Manager_Name: rec.manager_Name || rec.Manager_Name || "",
    Place_Type: rec.place_Type || rec.Place_Type || "Branch",
    Billing_Configuration_Toggle: rec.billing_Configuration_Toggle || rec.Billing_Configuration_Toggle || "F",
    GST_Number: rec.gsT_Number || rec.gst_Number || rec.GST_Number || "",
    API_Configuration_Toggle: rec.apI_Configuration_Toggle || rec.API_Configuration_Toggle || "F",
    API_URL_Link: rec.apI_URL_Link || rec.api_Url_Link || rec.API_URL_Link || "",
    API_Username: rec.apI_Username || rec.api_Username || rec.API_Username || "",
    API_password: rec.apI_password || rec.api_Password || rec.API_password || "",
    Branch_Status: rec.branch_Status || rec.Branch_Status || "A",
  };
}

// function validateAndBuildBranchRecord(form, existingRec, showToast) {
//   // Mandatory field check removed as per user request

//   return {
//     ...form,
//     Billing_Configuration_Toggle: form.Billing_Configuration_Toggle === true || form.Billing_Configuration_Toggle === "true" || form.Billing_Configuration_Toggle === "O" ? "O" : "F",
//     API_Configuration_Toggle: form.API_Configuration_Toggle === true || form.API_Configuration_Toggle === "true" || form.API_Configuration_Toggle === "O" ? "O" : "F",
//     Branch_Prefix: (form.Branch_Prefix || "").toUpperCase(),
//     GST_Number: (form.GST_Number || "").toUpperCase()
//   };
// }
function validateAndBuildBranchRecord(form, existingRec, showToast) {
  const errors = [];

  if (!form.Branch_Prefix || !form.Branch_Prefix.trim()) {
    errors.push("Branch Code is required");
  } else if (form.Branch_Prefix.trim().length < 2) {
    errors.push("Branch Code must be at least 2 letters");
  }

  if (!form.Place_name || !form.Place_name.trim()) {
    errors.push("Branch Name is required");
  }

  if (!form.City || !form.City.trim()) {
    errors.push("City is required");
  }

  if (!form.State_Code) {
    errors.push("State is required");
  }

  if (!form.Place_Type) {
    errors.push("Location Type (Branch/Delivery) is required");
  }

  if (form.Pincode && !/^\d{6}$/.test(form.Pincode)) {
    errors.push("Pincode must be exactly 6 digits");
  }

  if (form.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.Email)) {
    errors.push("Branch Email is not a valid email address");
  }

  if (form.Place_Contact && form.Place_Contact.length !== 10) {
    errors.push("Primary Contact must be a 10-digit number");
  }

  if (form.Billing_Configuration_Toggle === "O" && (!form.GST_Number || !form.GST_Number.trim())) {
    errors.push("GST Number is required when Billing Configuration is enabled");
  }

  if (form.API_Configuration_Toggle === "O") {
    if (!form.API_URL_Link || !form.API_URL_Link.trim()) errors.push("API URL is required when API Configuration is enabled");
    if (!form.API_Username || !form.API_Username.trim()) errors.push("API Username is required when API Configuration is enabled");
    if (!form.API_password || !form.API_password.trim()) errors.push("API Password is required when API Configuration is enabled");
  }

  if (errors.length > 0) {
    const message = errors.join("\n");
    showToast(errors[0], "error");
    alert("Please fix the following:\n\n" + message);
    return null;
  }

  return {
    ...form,
    Billing_Configuration_Toggle: form.Billing_Configuration_Toggle === true || form.Billing_Configuration_Toggle === "true" || form.Billing_Configuration_Toggle === "O" ? "O" : "F",
    API_Configuration_Toggle: form.API_Configuration_Toggle === true || form.API_Configuration_Toggle === "true" || form.API_Configuration_Toggle === "O" ? "O" : "F",
    Branch_Prefix: (form.Branch_Prefix || "").toUpperCase(),
    GST_Number: (form.GST_Number || "").toUpperCase()
  };
}